import {useTranslation} from 'react-i18next';
import {Checkbox} from '@mui/material';
import AppTextInput from '../../../../components/common/AppTextInput';
import {
  FormQuestion,
  FormQuestionOption,
} from '../../../../services/operations/forms';
import FilePreview from '../../../forms/components/AddEditForm/components/QuestionsTab/FilePreview';

const OTHER_MAX_LENGTH = 100;

/** Answer shape for a checkbox question */
export interface CheckboxAnswer {
  selected: string[]; // array of option ids, may include 'other'
  otherText?: string;
}

type Props = {
  question: FormQuestion;
  value: CheckboxAnswer | null;
  onChange: (value: CheckboxAnswer | null) => void;
};

function PublicCheckbox({question, value, onChange}: Props) {
  const {t} = useTranslation();

  const selectedIds = value?.selected ?? [];

  const isChecked = (optionId: string) => selectedIds.includes(optionId);

  const handleToggle = (optionId: string) => {
    const newSelected = isChecked(optionId)
      ? selectedIds.filter(id => id !== optionId)
      : [...selectedIds, optionId];

    if (newSelected.length === 0 && !value?.otherText) {
      onChange(null);
    } else {
      onChange({selected: newSelected, otherText: value?.otherText});
    }
  };

  const handleOtherTextChange = (text: string) => {
    if (text.length > OTHER_MAX_LENGTH) return;
    // Auto-check "other" when user starts typing
    const newSelected = isChecked('other')
      ? selectedIds
      : [...selectedIds, 'other'];
    onChange({selected: newSelected, otherText: text});
  };

  const handleClear = () => {
    onChange(null);
  };

  const otherOption = question?.options?.find(op => op?.isOther);

  return (
    <div className="flex flex-col gap-3 mt-3">
      <div className="flex flex-col gap-4">
        {/* Regular options */}
        {question?.options.map((option: FormQuestionOption) => {
          if (option.isOther) return null;
          return (
            <div key={option._id} className="flex flex-col">
              <div
                className="flex items-center gap-3 cursor-pointer"
                onClick={() => handleToggle(option._id!)}>
                {/* Checkbox square */}
                <Checkbox
                  color="secondary"
                  sx={{padding: 0}}
                  checked={isChecked(option._id!)}
                  onChange={() => handleToggle(option._id!)}
                />

                {/* Option label */}
                <span className="text-[16px] text-textColor">
                  {option.label}
                </span>
              </div>

              {/* Option file (below the option row) */}
              {option.image && (
                <div className="mt-2 flex justify-center">
                  <FilePreview file={option.image} />
                </div>
              )}
            </div>
          );
        })}

        {/* "Other" option — checkbox + full-width text input on the same row */}
        {otherOption && (
          <div className="flex flex-col">
            <div className="flex items-center gap-3">
              {/* Checkbox square */}

              <Checkbox
                color="secondary"
                sx={{padding: 0}}
                checked={isChecked('other')}
                onChange={() => handleToggle('other')}
              />

              {/* Full-width text input */}
              <AppTextInput
                maxLength={OTHER_MAX_LENGTH}
                value={value?.otherText ?? ''}
                onChange={e => handleOtherTextChange(e.target.value)}
                placeholder={t('forms.dialog.other')}
                className="border-none"
              />
            </div>

            {/* Other option file */}
            {otherOption?.image && (
              <div className="mt-2 flex justify-center">
                <FilePreview file={otherOption.image} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Clear selection link */}
      <div className="flex">
        <button
          type="button"
          onClick={handleClear}
          className="text-linkBlue underline hover:opacity-80 transition-opacity">
          {t('forms.public.clearSelection')}
        </button>
      </div>
    </div>
  );
}

export default PublicCheckbox;
